import { GridLayoutItem } from "@progress/kendo-react-layout";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConditionBuilder } from "@/components/ConditionBuilder/hooks/useConditionBuilder";
import {
  ConditionAttributeOption,
  ConditionOption,
} from "@/components/ConditionBuilder/types";
import { FormDropDownList } from "@/components/Forms";

interface ConditionOperatorFieldProps {
  fieldName: string;
  attributeFieldName: string;
}

const ConditionOperatorField: React.FC<ConditionOperatorFieldProps> = ({
  fieldName,
  attributeFieldName,
}) => {
  const { t } = useTranslation();
  const { valueGetter, config } = useConditionBuilder();
  const methods = useFormContext();
  const attribute = valueGetter(attributeFieldName) as
    | ConditionAttributeOption
    | undefined;

  // Always required when the row exists. To skip server rules entirely, the
  // user removes the row via the trash icon.
  const operatorValidator = useCallback(
    (value: unknown) => {
      const option = value as ConditionOption | undefined;
      return !option?.id ? t("validation.required") : undefined;
    },
    [t],
  );

  // Belt-and-suspenders registration: FormDropDownList wraps useController
  // which should register the validate rule, but for deeply-nested array
  // paths like `serverRules.0.operator` RHF has been observed to drop the
  // rule from re-validation tracking — so trigger() runs it once but
  // subsequent on-change re-validation never fires. Calling register here
  // ensures _fields[name]._f.validate is populated for both paths.
  useEffect(() => {
    methods.register(fieldName as never, {
      validate: (v: unknown) => {
        const msg = operatorValidator(v);
        return typeof msg === "string" && msg.length > 0 ? msg : true;
      },
    });
  }, [methods, fieldName, operatorValidator]);

  // Force re-validation when the field value changes. useController's
  // field.onChange path doesn't reliably fire validation for these
  // nested-array fields in reValidateMode "onChange" mode — the symptom is
  // that the "Required" inline error persists even after the user picks a
  // valid option. Subscribe via methods.watch (callback form) instead of
  // useWatch — useWatch returns a fresh deep-cloned reference on every
  // render even when the underlying value is unchanged, which would cause an
  // infinite loop if used as a useEffect dep.
  useEffect(() => {
    const subscription = methods.watch((_values, info) => {
      if (info?.name === fieldName && info?.type === "change") {
        void methods.trigger(fieldName as never);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, fieldName]);

  // Shared query with ConditionValueField — react-query deduplicates the call.
  const { data: resolvedMeta } = useQuery({
    queryKey: ["resolveAttribute", attribute?.id, attribute?.type],
    queryFn: () => config.resolveAttribute!(attribute!.id, attribute),
    enabled: !!attribute && !!config.resolveAttribute,
  });

  // Priority: resolveAttribute result > per-attribute static > global config
  const operators =
    resolvedMeta?.operators ?? attribute?.operators ?? config.operators;

  return (
    <GridLayoutItem>
      <FormDropDownList
        name={fieldName}
        data={operators}
        label={config.labels?.operator ?? "Operator"}
        floatingLabel={true}
        disabled={config.readOnly || !attribute?.id}
        disableAutoClear={true}
        validator={operatorValidator}
      />
    </GridLayoutItem>
  );
};

export default ConditionOperatorField;
